import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api, apiUpload, useAuth } from '@shop/api-client'
import type { Candidate, Category, Draft, ParseFlag, Warehouse } from '../api/types'
import { Perm } from '../api/types'
import { draftStatusName, formatMoment, formatMoney, formatQty, sourceName, toNumber } from '../ui/format'
import { ImageField } from '../ui/ImageField'
import {
  Banner,
  Button,
  Empty,
  ErrorBanner,
  Field,
  NumberInput,
  Panel,
  Row,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from '@shop/ui'

/**
 * ② المراجعةُ ثمّ ③ الاعتماد.
 *
 * <b>وكلُّ تصحيحٍ هنا يُكتب في `edited` إلى جانب ما فهمه المحلّل</b>: الفرقُ
 * بينهما هو ما يُصلَح به المحلّلُ بالقياس لا بالتخمين. ولهذا تُرسل التصحيحاتُ
 * حقلًا حقلًا (PATCH) ولا يُعاد بناءُ المسوّدة من الشاشة.
 */

/** رايةٌ واحدة — و`WARN` أوّلًا لأنّها التي تنتظر نظرًا. */
function Flag({ flag }: { flag: ParseFlag }) {
  const warn = flag.severity === 'WARN'

  return (
    <li className={`rounded-md border px-3 py-2 text-sm ${warn ? 'border-warn/50 bg-warn/10' : 'border-line bg-surface'}`}>
      <span className={warn ? 'font-medium' : 'text-ink-soft'}>{flag.message}</span>
      <span className="tabular block text-xs opacity-60" dir="ltr">
        {flag.code}
      </span>
    </li>
  )
}

function Duplicates({ draft, onDecide, busy }: {
  draft: Draft
  onDecide: (candidate: Candidate, decision: 'LINKED' | 'IGNORED') => void
  busy: boolean
}) {
  if (draft.candidates.length === 0) {
    return null
  }

  return (
    <Panel title="أصنافٌ شبيهة">
      <p className="mb-3 text-sm leading-relaxed text-ink-soft">
        لا يُدمج شيءٌ تلقائيًّا. «هذا هو» تربط المسوّدةَ بصنفٍ قائمٍ فلا يُنشأ صنفٌ
        ثانٍ، و«ليس هو» تُبقي إنشاءَ صنفٍ جديد.
      </p>

      <ul className="space-y-2">
        {draft.candidates.map((candidate) => (
          <li key={candidate.id} className="rounded-md border border-line p-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-sm font-medium">{candidate.productName}</span>
              <span className="tabular text-xs text-ink-soft" dir="ltr">
                {candidate.productCode}
                {candidate.sku ? ` · ${candidate.sku}` : ''}
              </span>
            </div>

            <p className="mt-1 text-xs text-ink-soft">
              {candidate.isBlocking ? 'تطابقٌ قاطع' : 'تشابهٌ يُقرأ'} · {candidate.matchKind}
              {candidate.matched ? ` · ${candidate.matched}` : ''}
            </p>

            {candidate.decision ? (
              <p className="mt-2 text-xs">
                قرارُك: <b>{candidate.decision === 'LINKED' ? 'هذا هو' : 'ليس هو'}</b>
              </p>
            ) : (
              <div className="mt-2 flex gap-2">
                <Button disabled={busy} onClick={() => onDecide(candidate, 'LINKED')}>
                  هذا هو — اربطها به
                </Button>
                <Button disabled={busy} onClick={() => onDecide(candidate, 'IGNORED')}>
                  ليس هو
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Panel>
  )
}

/**
 * القيمُ كما تُملأ في الحقول أوّلَ مرّة.
 *
 * <b>وتُملأ مرّةً واحدة</b> ثمّ لا يمسّها إلا الموظّف: لو زامنَت كلَّ إعادةِ
 * جلبٍ لَمَحَت ما يكتبه أثناء الكتابة. ويقع ذلك بإعادة بناء المكوّن عند تغيّر
 * المسوّدة (`key`) لا بأثرٍ يضبط حالة.
 */
function initialForm(d: Draft): Record<string, string> {
  return {
    nameAr: d.nameAr ?? '',
    brandText: d.brandText ?? '',
    modelText: d.modelText ?? '',
    colour: d.colour ?? '',
    capacity: d.capacity ?? '',
    barcode: d.barcode ?? '',
    qty: d.qty === null ? '' : String(d.qty),
    costPrice: d.costPrice === null ? '' : String(d.costPrice),
    sellPrice: d.sellPrice === null ? '' : String(d.sellPrice),
    warehouseId: d.warehouseId ?? '',
    notes: d.notes ?? '',
  }
}

export default function DraftReview() {
  const { id = '' } = useParams()

  const draft = useQuery({
    queryKey: ['draft', id],
    queryFn: () => api<Draft>(`/api/v1/intake/drafts/${id}`),
  })

  if (draft.isPending) {
    return <Spinner />
  }

  if (draft.isError) {
    return <ErrorBanner error={draft.error} />
  }

  // ‏`key` هو ما يُعيد بناءَ الحقول حين تُفتح مسوّدةٌ أخرى من القائمة: بلا هذا
  // يبقى المكوّنُ نفسُه ويبقى في حقوله اسمُ الصنف السابق.
  return <Review key={draft.data.id} draft={draft.data} draftId={id} />
}

function Review({ draft: initial, draftId: id }: { draft: Draft; draftId: string }) {
  const navigate = useNavigate()
  const queries = useQueryClient()
  const { can } = useAuth()

  const draft = useQuery({
    queryKey: ['draft', id],
    queryFn: () => api<Draft>(`/api/v1/intake/drafts/${id}`),
    initialData: initial,
  })

  const [form, setForm] = useState<Record<string, string>>(() => initialForm(initial))
  const [error, setError] = useState<unknown>(null)
  const [rejecting, setRejecting] = useState(false)

  // ⚠ **والقسمُ يُختار هنا لا في المسوّدة** — فلا عمودَ يُضاف ولا هجرة.
  //   والمُراجِعُ هو من يقرّر العائلةَ وهو ينظر إلى الصورة والاسم، لا من أملى
  //   الصنفَ في المخزن.
  const [categoryId, setCategoryId] = useState('')

  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => api<Category[]>('/api/v1/catalog/categories'),
    staleTime: 5 * 60_000,
  })
  const [rejectReason, setRejectReason] = useState('')
  const [imageBusy, setImageBusy] = useState(false)

  const warehouses = useQuery({
    queryKey: ['warehouses', draft.data.branchId],
    queryFn: () => api<Warehouse[]>(`/api/v1/warehouses?branchId=${draft.data.branchId}`),
    enabled: can(Perm.warehouseView),
  })

  const refresh = (fresh: Draft) => {
    queries.setQueryData(['draft', id], fresh)
    void queries.invalidateQueries({ queryKey: ['drafts'] })
  }

  const save = useMutation({
    mutationFn: () =>
      api<Draft>(`/api/v1/intake/drafts/${id}`, {
        method: 'PATCH',
        body: {
          nameAr: form.nameAr.trim() || null,
          brandText: form.brandText.trim() || null,
          modelText: form.modelText.trim() || null,
          colour: form.colour.trim() || null,
          capacity: form.capacity.trim() || null,
          barcode: form.barcode.trim() || null,
          qty: toNumber(form.qty),
          costPrice: toNumber(form.costPrice),
          sellPrice: toNumber(form.sellPrice),
          warehouseId: form.warehouseId || null,
          notes: form.notes.trim() || null,
        },
      }),
    onSuccess: refresh,
    onError: setError,
  })

  const decide = useMutation({
    mutationFn: (input: { candidateId: string; decision: string }) =>
      api<Draft>(`/api/v1/intake/drafts/${id}/duplicates/decide`, { method: 'POST', body: input }),
    onSuccess: refresh,
    onError: setError,
  })

  const confirm = useMutation({
    // ⚠ والحقولُ محفوظةٌ في المسوّدة، فيُحفظ قبله دائمًا — وإلا اعتُمد ما في
    //   القاعدة لا ما على الشاشة. **والقسمُ وحدَه يُرسل في الجسم**: ليس حقلًا
    //   من حقول المسوّدة بل قرارُ المُراجِع لحظةَ الاعتماد.
    mutationFn: async () => {
      await save.mutateAsync()

      return api<Draft>(`/api/v1/intake/drafts/${id}/confirm`, {
        method: 'POST',
        body: { categoryId: categoryId === '' ? null : categoryId },
      })
    },
    onSuccess: refresh,
    onError: setError,
  })

  const reject = useMutation({
    mutationFn: () =>
      api<Draft>(`/api/v1/intake/drafts/${id}/reject`, { method: 'POST', body: { reason: rejectReason.trim() } }),
    onSuccess: (fresh) => {
      refresh(fresh)
      setRejecting(false)
    },
    onError: setError,
  })

  const d = draft.data
  const busy = save.isPending || decide.isPending || confirm.isPending || reject.isPending
  const set = (key: string) => (value: string) => setForm((current) => ({ ...current, [key]: value }))
  const warns = d.flags.filter((f) => f.severity === 'WARN')
  const infos = d.flags.filter((f) => f.severity !== 'WARN')
  const pendingBlocker = d.candidates.some((c) => c.isBlocking && c.decision === null)
  const priceWithoutRight = toNumber(form.sellPrice) !== null && !can(Perm.priceEdit)

  // الاعتمادُ متكرّرُ الأثر (idempotent) على الخادم: إعادةُ النداء تردّ النتيجةَ
  // نفسَها ولا تُنشئ حركةً ثانية. فالشاشةُ تعرض النتيجةَ ولا تخاف الضغطَ مرّتين.
  if (d.isFinal) {
    return (
      <>
        <Panel title={`${d.draftNo} — ${draftStatusName(d.status)}`}>
          <Row label="الاسم">{d.nameAr ?? '—'}</Row>
          <Row label="الكميّة">{formatQty(d.qty)}</Row>
          <Row label="الكلفة">{formatMoney(d.costPrice, d.currency)}</Row>
          <Row label="سعر البيع">{formatMoney(d.sellPrice, d.currency)}</Row>
          <Row label="وقت الاعتماد">{formatMoment(d.confirmedAt)}</Row>
        </Panel>

        {d.status === 'REJECTED' && <Banner tone="warn">رُفضت هذه المسوّدة، ولم يُنشأ منها شيء.</Banner>}

        <div className="flex flex-wrap gap-2">
          {d.productId && (
            <Link to={`/products/${d.productId}`}>
              <Button tone="primary">افتح الصنف</Button>
            </Link>
          )}
          <Button onClick={() => navigate('/intake')}>إدخالٌ جديد</Button>
          <Button onClick={() => navigate('/drafts')}>مسوّداتي</Button>
        </div>
      </>
    )
  }

  return (
    <>
      <Panel title={`${d.draftNo} — ${draftStatusName(d.status)}`}>
        <Row label="المصدر">{sourceName(d.source)}</Row>
        <Row label="فُتحت">{formatMoment(d.createdAt)}</Row>
      </Panel>

      {/* الصورةُ قبل النصّ: المراجعُ يحكم على «سمّاعة أنكر» أهي التي في الصورة —
          والحكمُ بالعين أسرعُ من قراءة ثلاثة حقول. */}
      <Panel title="الصورة">
        <ImageField
          path={d.hasImage ? `/api/v1/intake/drafts/${d.id}/image` : null}
          readOnly={d.isFinal || !can(Perm.draftEdit)}
          busy={imageBusy}
          onPick={(file) => {
            setImageBusy(true)
            apiUpload(`/api/v1/intake/drafts/${d.id}/image`, file)
              .then(() => draft.refetch())
              .finally(() => setImageBusy(false))
          }}
          onRemove={
            d.hasImage
              ? () => {
                  setImageBusy(true)
                  api(`/api/v1/intake/drafts/${d.id}/image`, { method: 'DELETE' })
                    .then(() => draft.refetch())
                    .finally(() => setImageBusy(false))
                }
              : undefined
          }
          hint={d.isFinal ? undefined : 'تنتقل إلى الصنف عند الاعتماد إن كان جديدًا.'}
        />
      </Panel>

      <Panel title="ما وصل الخادم">
        {d.inputs.length === 0 ? (
          <Empty>لا مدخلات.</Empty>
        ) : (
          <ul className="space-y-2">
            {d.inputs.map((input) => (
              <li key={input.id} className="rounded-md border border-line bg-surface px-3 py-2 text-sm">
                <span className="text-xs text-ink-soft">{sourceName(input.kind)} · {formatMoment(input.createdAt)}</span>
                <p className="mt-1 leading-relaxed">{input.rawText ?? '—'}</p>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {d.flags.length > 0 && (
        <Panel title="ما يستوجب نظرَك">
          <ul className="space-y-2">
            {[...warns, ...infos].map((flag, index) => (
              <Flag key={`${flag.code}-${index}`} flag={flag} />
            ))}
          </ul>
        </Panel>
      )}

      <Panel title="الحقول">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="الاسم بالعربيّة">
            <TextInput value={form.nameAr} onChange={set('nameAr')} disabled={busy} />
          </Field>
          <Field label="الماركة">
            <TextInput value={form.brandText} onChange={set('brandText')} disabled={busy} />
          </Field>
          <Field label="الطراز">
            <TextInput value={form.modelText} onChange={set('modelText')} disabled={busy} />
          </Field>
          <Field label="اللون">
            <TextInput value={form.colour} onChange={set('colour')} disabled={busy} />
          </Field>
          <Field label="السعة">
            <TextInput value={form.capacity} onChange={set('capacity')} disabled={busy} />
          </Field>
          <Field label="الباركود">
            <TextInput value={form.barcode} onChange={set('barcode')} dir="ltr" disabled={busy} />
          </Field>
          <Field label="الكميّة" hint="اتركها فارغةً لاعتمادِ الصنف بلا رصيد.">
            <NumberInput value={form.qty} onChange={set('qty')} disabled={busy} />
          </Field>
          <Field label={`الكلفة (${d.currency})`} hint="كلفةُ هذه الحركة — لا تتغيّر بتغيّر سعر البيع لاحقًا.">
            <NumberInput value={form.costPrice} onChange={set('costPrice')} disabled={busy} />
          </Field>
          <Field label={`سعر البيع (${d.currency})`}>
            <NumberInput value={form.sellPrice} onChange={set('sellPrice')} disabled={busy} />
          </Field>
          <Field label="المستودع" hint="الكميّةُ تحتاج مستودعًا مخوَّلًا وفترةً افتتاحيّةً مفتوحة.">
            <Select value={form.warehouseId} onChange={set('warehouseId')} disabled={busy}>
              <option value="">بلا مستودع (صنفٌ فقط)</option>
              {(warehouses.data ?? []).filter((w) => w.isActive).map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.nameAr}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="mt-4">
          <Field label="ملاحظات">
            <TextArea value={form.notes} onChange={set('notes')} rows={2} disabled={busy} />
          </Field>
        </div>
      </Panel>

      <Duplicates
        draft={d}
        busy={busy}
        onDecide={(candidate, decision) => decide.mutate({ candidateId: candidate.id, decision })}
      />

      {pendingBlocker && (
        <Banner tone="warn">
          تطابقٌ قاطعٌ لم يُحسم بعد: اربط المسوّدةَ بالصنف القائم أو صحّح الرقم قبل
          الاعتماد.
        </Banner>
      )}

      {priceWithoutRight && (
        <Banner tone="warn">
          المسوّدةُ تحمل سعرَ بيع، واعتمادُ السعر صلاحيّةٌ لا تملكها. سيردّ الخادمُ
          الاعتماد — امسح السعرَ لتعتمدها، أو اترك الاعتمادَ لمخوَّل.
        </Banner>
      )}

      <ErrorBanner error={error} />

      {confirm.error instanceof ApiError && confirm.error.code === 'intake.price_needs_permission' && (
        <Banner tone="warn">
          لم يُعتمد شيء — ولم يُنشأ صنفٌ ولا حركة. المعاملةُ كلُّها رجعت.
        </Banner>
      )}

      {/* ⚠ **ولا اعتمادَ بلا قسمٍ حين يكون الاعتمادُ إنشاءَ صنفٍ جديد.**
          والخادمُ يردّ `catalog.category_required`؛ وهذا يمنع الوصولَ إلى
          رفضه. وفي الربط بصنفٍ قائمٍ لا يُسأل: الصنفُ له قسمُه أصلًا. */}
      <div className="mb-3 max-w-sm">
        <Field
          label="القسم"
          hint="عائلةُ المادّة — إلزاميّةٌ للصنف الجديد، وتُترك فارغةً عند الربط بصنفٍ قائم."
        >
          <Select value={categoryId} onChange={setCategoryId} disabled={busy}>
            <option value="">اختر القسم…</option>
            {(categories.data ?? []).filter((c) => c.isActive).map((category) => (
              <option key={category.id} value={category.id}>{category.nameAr}</option>
            ))}
          </Select>
        </Field>
      </div>

      {confirm.error instanceof ApiError && confirm.error.code === 'catalog.category_required' && (
        <Banner tone="warn">
          لم يُعتمد شيء. <b>اختر القسمَ أوّلًا</b> — ولا يدخل الكتالوجَ صنفٌ بلا
          عائلةٍ يُعرف بها.
        </Banner>
      )}

      <div className="flex flex-wrap gap-2">
        <Button tone="primary" disabled={busy || !can(Perm.draftConfirm)} onClick={() => confirm.mutate()}>
          {confirm.isPending ? 'يُعتمد…' : 'اعتماد'}
        </Button>

        <Button disabled={busy} onClick={() => save.mutate()}>
          {save.isPending ? 'يُحفظ…' : 'حفظُ التصحيحات'}
        </Button>

        <Button tone="danger" disabled={busy} onClick={() => setRejecting((current) => !current)}>
          رفض
        </Button>
      </div>

      {rejecting && (
        <Panel title="سببُ الرفض">
          <p className="mb-3 text-sm text-ink-soft">
            يبقى في السجلّ لأنّه جوابُ «لماذا لم يُدخل هذا الصنف؟».
          </p>
          <TextArea value={rejectReason} onChange={setRejectReason} rows={2} disabled={busy} />
          <div className="mt-3">
            <Button tone="danger" disabled={busy || rejectReason.trim().length === 0} onClick={() => reject.mutate()}>
              تأكيدُ الرفض
            </Button>
          </div>
        </Panel>
      )}
    </>
  )
}
